import { useState, useEffect, useCallback, useRef } from 'react';
import api, { apiRoutes } from '../api';
import { getConfig } from '../config/environment';
import { SQUARE_GRID_POLL_MS, SQUARE_GRID_POLL_MAX_MS } from '../config/squareConstants';

// When SignalR is live the hub is the primary source of truth; polling acts
// as a heartbeat / reconciliation check only.
const SQUARE_GRID_HEARTBEAT_MS = 120000;

/**
 * useSquareBoardData — fetches per-square state for a Square Game draw.
 *
 * Polls on an interval so short-lived `held` reservations (created when another
 * user adds a square to their cart) become visible on passive pages like the
 * homepage. Polling pauses while the tab is hidden and resumes on focus.
 *
 * On fetch failures the interval does **not** stop — it backs off
 * exponentially (cap `SQUARE_GRID_POLL_MAX_MS`) and resets on the next
 * successful fetch.
 *
 * When a SignalR connection is active (`signalRConnected: true`), the effective
 * poll interval is relaxed to a long heartbeat (`SQUARE_GRID_HEARTBEAT_MS`)
 * since the hub delivers events more promptly than any reasonable poll. If
 * the hub drops the hook automatically returns to the normal cadence.
 *
 * Consumers can call the returned `applyEvent({ type, squareNumber, ... })`
 * helper to patch the cached grid state in response to SignalR pushes
 * without waiting for the next poll.
 *
 * @param {object} mainDraw - the main draw object (must have `id`)
 * @param {object} [options]
 * @param {number} [options.pollMs=SQUARE_GRID_POLL_MS] - polling interval in ms (set 0 to disable)
 * @param {boolean} [options.signalRConnected=false] - when true, slow the poll to a heartbeat
 * @returns {{ squares: Array<{number, status, heldUntilUtc?, memberMaskedName?}>,
 *            loading: boolean,
 *            error: Error|null,
 *            applyEvent: (event:object) => void }}
 *
 * Status is passed through from the API: 'available' | 'taken' | 'sold' | 'held'.
 * On error or no drawId: returns squares: [] (consumer falls back to proportional).
 */
export default function useSquareBoardData(mainDraw, options = {}) {
  const { pollMs = SQUARE_GRID_POLL_MS, signalRConnected = false } = options;
  const [squares, setSquares] = useState([]);
  const [squareGameId, setSquareGameId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stable reference so applyEvent doesn't churn on every render.
  const squaresRef = useRef(squares);
  useEffect(() => { squaresRef.current = squares; }, [squares]);

  const effectivePollMs = signalRConnected && pollMs > 0
    ? Math.max(pollMs, SQUARE_GRID_HEARTBEAT_MS)
    : pollMs;

  useEffect(() => {
    if (!mainDraw?.id) {
      setSquares([]);
      return;
    }
    let cancelled = false;
    let timerId = null;
    let consecutiveErrors = 0;

    const nextDelay = () => {
      if (!effectivePollMs) return 0;
      if (consecutiveErrors === 0) return effectivePollMs;
      // Exponential backoff capped at SQUARE_GRID_POLL_MAX_MS.
      const backoff = effectivePollMs * Math.pow(2, consecutiveErrors - 1);
      return Math.min(backoff, SQUARE_GRID_POLL_MAX_MS);
    };

    const fetchOnce = async () => {
      try {
        const { tlfId } = getConfig();
        const res = await api.get(apiRoutes.SquareGameGrid, {
          params: { organizationId: tlfId, drawId: mainDraw.id }
        });
        const gridData = res.data?.payload?.squares || res.data?.squares || [];
        const gameId = res.data?.payload?.squareGameId || res.data?.squareGameId || null;
        const normalized = gridData.map(sq => ({
          number: sq.number ?? sq.squareNumber,
          status: sq.status || 'available',
          heldUntilUtc: sq.heldUntilUtc || sq.holdExpiresAt || null,
          memberMaskedName: sq.memberMaskedName || sq.maskedName || null,
        }));
        if (!cancelled) {
          setSquares(normalized);
          setSquareGameId(gameId);
          setError(null);
          consecutiveErrors = 0;
        }
      } catch (err) {
        if (!cancelled) {
          consecutiveErrors += 1;
          console.warn(
            `[useSquareBoardData] fetch failed (attempt ${consecutiveErrors}); will retry in ${nextDelay()}ms`,
            err
          );
          setError(err);
        }
      }
    };

    const scheduleNext = () => {
      if (cancelled || !effectivePollMs) return;
      timerId = window.setTimeout(async () => {
        if (cancelled) return;
        if (typeof document !== 'undefined' && document.hidden) {
          scheduleNext();
          return;
        }
        await fetchOnce();
        scheduleNext();
      }, nextDelay());
    };

    const run = async () => {
      const isInitialLoad = !Array.isArray(squaresRef.current) || squaresRef.current.length === 0;
      if (isInitialLoad) setLoading(true);
      await fetchOnce();
      if (!cancelled && isInitialLoad) setLoading(false);
      scheduleNext();
    };

    const handleVisibility = () => {
      if (!cancelled && typeof document !== 'undefined' && !document.hidden) {
        fetchOnce();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility);
    }

    run();

    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
    };
  }, [mainDraw?.id, effectivePollMs]);

  /**
   * Patch the cached grid in response to a real-time SignalR event.
   *
   * Event shape:
   *   { type: 'held'|'released'|'purchased'|'winner', squareNumber, heldUntilUtc?, memberMaskedName? }
   *
   * Unknown types and non-matching cells are safely ignored.
   */
  const applyEvent = useCallback((event) => {
    if (!event || typeof event !== 'object') return;
    const { type } = event;
    const squareNumber = Number(event.squareNumber);
    if (!Number.isFinite(squareNumber) || squareNumber <= 0) return;

    setSquares(prev => {
      const src = Array.isArray(prev) ? prev : [];
      const existingIdx = src.findIndex(s => Number(s.number) === squareNumber);
      
      // If the square doesn't exist in the grid yet, ignore the event.
      // Real-time events should only update existing squares, never create new ones.
      if (existingIdx < 0) {
        console.warn(`[useSquareBoardData] Ignoring event for unknown square #${squareNumber}`);
        return prev;
      }

      const current = src[existingIdx];
      let next;
      switch (type) {
        case 'held':
          next = { ...current, status: 'held', heldUntilUtc: event.heldUntilUtc || null };
          break;
        case 'released':
          // Only downgrade holds — don't revert a purchased cell.
          if (current.status !== 'held') return prev;
          next = { ...current, status: 'available', heldUntilUtc: null };
          break;
        case 'purchased':
          next = {
            ...current,
            status: 'taken',
            heldUntilUtc: null,
            memberMaskedName: event.memberMaskedName || current.memberMaskedName || null,
          };
          break;
        case 'winner':
          next = {
            ...current,
            isWinner: true,
            memberMaskedName: event.memberMaskedName || current.memberMaskedName || null,
          };
          break;
        default:
          return prev;
      }

      const copy = src.slice();
      copy[existingIdx] = next;
      return copy;
    });
  }, []);

  return { squares, squareGameId, loading, error, applyEvent };
}

